import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LuLogOut, LuShoppingCart, LuPackage, LuFileText, LuBox, LuLayoutDashboard, LuUser } from "react-icons/lu";
import { logoutPortal, fetchPortalBusiness } from '../../redux/reducers/portalAuthSlice';
import { getCart } from '../../redux/reducers/portalCartSlice';
import { Container, Nav, Navbar, Badge, Button, Spinner } from 'react-bootstrap';
import PortalDashboard from '../../pages/Portal/PortalDashboard';
import ConfirmModal from '../ConfirmModal';

const PortalLayout = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { businessId } = useParams();
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const { customer, portalToken, loading: authLoading } = useSelector((state) => state.portalAuth);
    const { cart, loading: cartLoading } = useSelector((state) => state.portalCart);

    useEffect(() => {
        if (!portalToken) {
            navigate('/customer-side/login');
        } else {
            dispatch(getCart());
            // Fetch business name if missing from customer object or if businessId changed
            dispatch(fetchPortalBusiness(businessId));
        }
    }, [portalToken, navigate, dispatch, businessId]);

    const handleLogout = () => {
        setShowLogoutModal(true);
    };

    const confirmLogout = () => {
        dispatch(logoutPortal());
        navigate('/customer-side/login');
    };

    if (!customer) return null;

    const isLoading = authLoading || cartLoading;

    return (
        <div className="portal-wrapper bg-light min-vh-100 d-flex flex-column">
            <Navbar bg="white" expand="lg" className="border-bottom sticky-top py-3 shadow-sm">
                <Container>
                    <Navbar.Brand as={Link} to={`/customer-side/${businessId}/dashboard`} className="d-flex align-items-center gap-2">
                        <div className="rounded-2" style={{ width: 4, height: 28, background: "#28B0C4" }} />
                        <span className="fw-bold text-dark fs-4">{customer.businessName || 'Customer Portal'}</span>
                    </Navbar.Brand>

                    <Navbar.Toggle aria-controls="portal-nav" />

                    <Navbar.Collapse id="portal-nav">
                        <Nav className="mx-auto gap-3">
                            <Nav.Link as={Link} to={`/customer-side/${businessId}/products`} active={location.pathname.includes('/products')}>
                                <LuBox size={18} className="me-2" /> Products
                            </Nav.Link>
                            <Nav.Link as={Link} to={`/customer-side/${businessId}/orders`} active={location.pathname.includes('/orders')}>
                                <LuPackage size={18} className="me-2" /> My Orders
                            </Nav.Link>
                            <Nav.Link as={Link} to={`/customer-side/${businessId}/invoices`} active={location.pathname.includes('/invoices')}>
                                <LuFileText size={18} className="me-2" /> My Invoices
                            </Nav.Link>
                            <Nav.Link as={Link} to={`/customer-side/${businessId}/profile`} active={location.pathname.includes('/profile')}>
                                <LuUser size={18} className="me-2" /> My Profile
                            </Nav.Link>
                        </Nav>
                        
                        <Nav className="align-items-center gap-3">
                            {isLoading && (
                                <Spinner animation="border" size="sm" style={{ color: "#28B0C4" }} className="me-2" />
                            )}
                            <Nav.Link as={Link} to={`/customer-side/${businessId}/cart`} className="position-relative p-2">
                                <LuShoppingCart size={24} color="#28B0C4" />
                                {cart?.items?.length > 0 && (
                                    <Badge pill bg="danger" className="position-absolute top-0 start-100 translate-middle" style={{ fontSize: '10px' }}>
                                        {cart.items.length}
                                    </Badge>
                                )}
                            </Nav.Link>
                            
                            <div className="d-flex align-items-center gap-2 ms-lg-3 border-start ps-lg-3">
                                <div className="text-end d-none d-sm-block">
                                    <div className="fw-semibold text-dark small">{customer.name}</div>
                                    <div className="text-muted" style={{ fontSize: '11px' }}>Customer</div>
                                </div>
                                <Button variant="outline-danger" size="sm" onClick={handleLogout} className="rounded-3 p-2">
                                    <LuLogOut size={16} />
                                </Button>
                            </div>
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>

            <main className="flex-grow-1 py-5">
                <Container style={{ maxWidth: '1200px' }}>
                    <Outlet />
                </Container>
            </main>
            
            <footer className="py-4 mt-auto border-top bg-white">
                <Container className="text-center text-muted small">
                    © 2026 Powered by Seven Tech
                </Container>
            </footer>
            <ConfirmModal
                show={showLogoutModal}
                onHide={() => setShowLogoutModal(false)}
                onConfirm={confirmLogout}
                title="Confirm Logout"
                message="Are you sure you want to end your session? You will need to login again to access your portal."
                confirmText="Yes, Logout"
                variant="danger"
                icon={LuLogOut}
            />
        </div>
    );
};

export default PortalLayout;
